// @core/stores/fiscalization/FiscalizationShiftStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { fiscalizationApi } from '@core/api';
import type {
  CloseShiftRequest,
  CloseShiftResponse,
  FiscalShift,
  GetShiftReportResponse,
  OpenShiftRequest,
  OpenShiftResponse,
} from '@core/types/fiscalization';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class FiscalizationShiftStore {
  // List state
  items: FiscalShift[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: FiscalShift | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, FiscalShift>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): FiscalShift | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: FiscalShift | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Close fiscal shift
   */
  async CloseShift(fiscalPointId: string, data: CloseShiftRequest): Promise<CloseShiftResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.CloseShift(fiscalPointId, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get shift report
   */
  async GetShiftReport(fiscalPointId: string, shiftId: string): Promise<GetShiftReportResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.GetShiftReport(fiscalPointId, shiftId);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List shifts
   */
  async ListShifts(fiscalPointId: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.ListShifts(fiscalPointId, params);

      runInAction(() => {
        this.items = response.fiscal_shifts?.shifts ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Open fiscal shift
   */
  async OpenShift(fiscalPointId: string, data: OpenShiftRequest): Promise<OpenShiftResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.OpenShift(fiscalPointId, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const fiscalizationShiftStore = new FiscalizationShiftStore();
